/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable */
//@ts-nocheck

import { CSVparser } from "./parseLibrary.types";

//parsing logic
export function parseCSV(input: string): CSVparser {
  // destructuring the config object
  let delimiter = ",";
  let newline = "\n";
  let comments = "#";
  let quoteChar = '"';
  let escapeChar = quoteChar;
  let cursor = 0;
  let aborted = false;

  //This function helps to escape special characters in a given string to prevents the special characters from being interpreted as regex metacharacters.
  function escapeSpecialChars(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  //The main parsing logic is here
  function parser(input: string) {
    let inputLength = input.length;
    let delimLength = delimiter.length;
    let newlineLength = newline.length;
    let commentsLength = comments.length;

    // initialize variables
    cursor = 0;
    let data: string[] = []; // to hold parsed data
    let errors: {
      type: string;
      code: string;
      message: string;
      row: number;
      index: number;
    }[] = []; // to hold errors found while parsing

    let row: string[] = []; // to hold a line or row of csv
    let lastCursor = 0;

    // Returns an object with the parsing results, errors, and meta data. */
    function returnParsedData(stopped?: boolean): {
      data: string[];
      errors: {
        type: string;
        code: string;
        message: string;
        row: number;
        index: number;
      }[];
      meta: {
        delimiter: string;
        linebreak: string;
        aborted: boolean;
        truncated: boolean;
        cursor: number;
      };
    } {
      return {
        data: data,
        errors: errors,
        meta: {
          delimiter: delimiter,
          linebreak: newline,
          aborted: aborted,
          truncated: !!stopped,
          cursor: lastCursor,
        },
      };
    }

    //This function checks if the input is a string. If not, it throws an error. This function also checks If the input string is empty, it immediately returns an empty result.
    const handleInvalidInput = (input: string) => {
      if (typeof input !== "string") throw new Error("Input must be a string");
      if (!input) return returnParsedData();
    };

    function pushRow(row: any) {
      data.push(row);
      lastCursor = cursor;
    }

    // If the input string is empty or invalid, it immediately returns an empty result.
    handleInvalidInput(input);

    // Finds the position of the next delimiter starting from the current cursor position.
    let nextDelimiterPosition = input.indexOf(delimiter, cursor);

    //Finds the position of the next newline starting from the current cursor position.
    let nextNewlinePosition = input.indexOf(newline, cursor);

    //Creates a regular expression to match escaped quote characters.
    let quoteCharRegex = new RegExp(
      escapeSpecialChars(escapeChar) + escapeSpecialChars(quoteChar),
      "g"
    );

    //Finds the position of the next quote character starting from the current cursor position.
    let quotePosition = input.indexOf(quoteChar, cursor);

    // This function calculate number of extra spaces after closing quote and the provided index parameter.
    function calculateExtraSpaces(index: number): number {
      let spaceLength = 0;
      if (index !== -1) {
        let textBetweenClosingQuoteAndIndex = input.substring(
          quotePosition + 1,
          index
        );
        if (
          textBetweenClosingQuoteAndIndex &&
          textBetweenClosingQuoteAndIndex.trim() === ""
        ) {
          spaceLength = textBetweenClosingQuoteAndIndex.length;
        }
      }
      return spaceLength;
    }

    //  Appends the remaining input from cursor to the end into row, saves the row, and returns the results.
    function finalizeParsing(value?: string | undefined) {
      let newvalue = "";
      if (value === undefined) {
        newvalue = input.substring(cursor);
        row.push(newvalue);
      } else {
        row.push(value);
      }
      cursor = inputLength;
      pushRow(row);
      return returnParsedData();
    }

    // Appends the current row to the results. It sets the cursor to newCursor and finds the nextNewlinePosition.
    function saveCurrentRow(newCursor: number) {
      cursor = newCursor;
      pushRow(row);
      row = [];
      nextNewlinePosition = input.indexOf(newline, cursor);
    }

    //This function handles the case where no quotes are found in the input csv file.
    const fastModeParsing = (input: string) => {
      let rows = input.split(newline);
      for (let i = 0; i < rows.length; i++) {
        let row: string | string[] = rows[i];
        cursor += row.length;
        if (i !== rows.length - 1) cursor += newline.length;
        if (comments && row.substring(0, commentsLength) === comments) continue;
        let result = row.split(delimiter);
        pushRow(result);
      }
    };

    //This function ensures that unquoted-fields are extracted correctly based on the cursor, delimiter and newline positions.
    const handleUnquotedFieldParsing = () => {
      row.push(input.substring(cursor, nextDelimiterPosition));
      cursor = nextDelimiterPosition + delimLength;
      nextDelimiterPosition = input.indexOf(delimiter, cursor);
    };

    //This function handles comments within CSV data.It ensures that comments are skipped and do not interfere with the parsing of actual data.
    const handleCommentedRows = () => {
      if (nextNewlinePosition === -1)
        // Comment ends at end of file
        return returnParsedData();
      cursor = nextNewlinePosition + newlineLength;
      nextNewlinePosition = input.indexOf(newline, cursor);
      nextDelimiterPosition = input.indexOf(delimiter, cursor);
    };

    //This function handles the scenario when end of row is reached.It extracts the remaining value of the row and saves it.
    const handleEndOfRow = () => {
      row.push(input.substring(cursor, nextNewlinePosition));
      saveCurrentRow(nextNewlinePosition + newlineLength);
    };

    const pushError = ({
      type,
      code,
      message,
      row,
      index,
    }: {
      type: string;
      code: string;
      message: string;
      row: number;
      index: number;
    }) => {
      errors.push({
        type,
        code,
        message,
        row,
        index,
      });
    };

    //This function handles closing quote at end of file
    const handleClosingQuote = () => {
      let value = input
        .substring(cursor, quotePosition)
        .replace(quoteCharRegex, quoteChar);
      return finalizeParsing(value);
    };

    if (!input.includes(quoteChar)) {
      //This function is for simple CSV parsing without quotes for optimization.
      fastModeParsing(input);
      return returnParsedData();
    }

    // Parser loop
    let i = 0;
    let j = 0;
    for (;;) {
      // Checking if field has an opening quote
      if (input[cursor] === quoteChar) {
        quotePosition = cursor;
        // Skipping the opening quote
        cursor++;
        // loop to parse quoted field
        for (;;) {
          // to find matching closing quote
          quotePosition = input.indexOf(quoteChar, quotePosition + 1);
          //checking if matching closing quote not found
          if (quotePosition === -1) {
            pushError({
              type: "Quotes",
              code: "MissingQuotes",
              message: "Quoted field unterminated",
              row: data.length,
              index: cursor,
            });
            return finalizeParsing();
          }

          // checking if closing quote is found at end of file
          if (quotePosition === inputLength - 1) {
            handleClosingQuote();
          }

          //in case, if escape quote is found, we skip it and parser will treat it as part of data
          //to find position of opening escaped quote
          if (
            quoteChar === escapeChar &&
            input[quotePosition + 1] === escapeChar
          ) {
            quotePosition++;
            continue;
          }
          //to find position of closing escaped quote
          if (
            quoteChar !== escapeChar &&
            quotePosition !== 0 &&
            input[quotePosition - 1] === escapeChar
          ) {
            continue;
          }

          // updating position of delimiter and newline to ensure delimiter/newline found inside quoted fields are not misunderstood
          if (
            nextDelimiterPosition !== -1 &&
            nextDelimiterPosition < quotePosition + 1
          ) {
            nextDelimiterPosition = input.indexOf(delimiter, quotePosition + 1);
          }

          if (
            nextNewlinePosition !== -1 &&
            nextNewlinePosition < quotePosition + 1
          ) {
            nextNewlinePosition = input.indexOf(newline, quotePosition + 1);
          }
          //  to determine the end of the current field in a CSV file
          let nearestDelimiterOrNewline =
            nextNewlinePosition === -1
              ? nextDelimiterPosition
              : Math.min(nextDelimiterPosition, nextNewlinePosition);

          let spacesAfterQuote = calculateExtraSpaces(
            nearestDelimiterOrNewline
          );
          // extracting quoted field value to the current row
          if (
            input[quotePosition + spacesAfterQuote + delimLength] === delimiter
          ) {
            row.push(
              input
                .substring(cursor, quotePosition)
                .replace(quoteCharRegex, quoteChar)
            );
            //updates the cursor position to the position after the delimiter,
            cursor = quotePosition + 1 + spacesAfterQuote + delimLength;
            // If char after following delimiter is not quoteChar, we find next quote character position
            if (
              input[quotePosition + 1 + spacesAfterQuote + delimLength] !==
              quoteChar
            ) {
              quotePosition = input.indexOf(quoteChar, cursor);
            }
            nextDelimiterPosition = input.indexOf(delimiter, cursor);
            nextNewlinePosition = input.indexOf(newline, cursor);
            break;
          }

          let spacesBetweenQuoteAndNewLine =
            calculateExtraSpaces(nextNewlinePosition);

          // condition to check if the closing quote (quotePosition) is followed by a newline character, possibly with spaces.
          if (
            input.substring(
              quotePosition + 1 + spacesBetweenQuoteAndNewLine,
              quotePosition + 1 + spacesBetweenQuoteAndNewLine + newlineLength
            ) === newline
          ) {
            row.push(
              input
                .substring(cursor, quotePosition)
                .replace(quoteCharRegex, quoteChar)
            );
            saveCurrentRow(
              quotePosition + 1 + spacesBetweenQuoteAndNewLine + newlineLength
            );
            nextDelimiterPosition = input.indexOf(delimiter, cursor);
            quotePosition = input.indexOf(quoteChar, cursor);
            break;
          }
          // Checking invalid closing quotes
          pushError({
            type: "Quotes",
            code: "InvalidQuotes",
            message: "Trailing quote on quoted field is malformed",
            row: data.length,
            index: cursor,
          });

          quotePosition++;
          j++;
          continue;
        }

        continue;
      }

      //to check commented out rows
      if (
        comments &&
        row.length === 0 &&
        input.substring(cursor, cursor + commentsLength) === comments
      ) {
        handleCommentedRows();
        continue;
      }

      //to check unquoted fields
      if (
        nextDelimiterPosition !== -1 &&
        (nextDelimiterPosition < nextNewlinePosition ||
          nextNewlinePosition === -1)
      ) {
        handleUnquotedFieldParsing();
        continue;
      }

      // to check end of row
      if (nextNewlinePosition !== -1) {
        handleEndOfRow();
        continue;
      }
      break;
    }
    return finalizeParsing();
  }
  return parser(input);
}
